import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { Skeleton } from '@mui/material';

import { VideaTypography } from '../../common-components/elements/VideaTypography/VideaTypography';
import { useGetPatientFindings } from '../../react-query/useInsightsRecap';
import { green400 } from '../../theme/videa-style-tokens';

import {
  Card,
  Flex,
  SubCard,
  TipsAndUpdatesOutlinedIcon,
} from './CommonStyles';

const FindingsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2em;
  padding: 2em 0;
`;

const SubcardTitleRow = styled(VideaTypography)`
  display: flex;
  align-items: center;
  gap: 0.5em;
`;

export const PatientFindingsCard = () => {
  const { data, isLoading } = useGetPatientFindings();

  const recapPatientsWithAnalysis =
    data?.recapPeriod?.patientAnalyses?.patientsWithAnalysis || 0;
  const recapPatientsWithCaries =
    data?.recapPeriod?.patientAnalyses?.patientsWithCariesFindings || 0;

  const timeSavingsValue = (Number(recapPatientsWithAnalysis) * 3.4).toFixed();

  if (isLoading) {
    return (
      <Card>
        <Skeleton variant="rectangular" height="2em" data-testid="skeleton" />
        <Flex className="center space-between">
          <Skeleton
            variant="rectangular"
            height="2em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="2em"
            width="2em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="rectangular"
            height="2em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="2em"
            width="2em"
            data-testid="skeleton"
          />
        </Flex>
        <Skeleton variant="rectangular" height="8em" data-testid="skeleton" />
      </Card>
    );
  }

  if (!recapPatientsWithAnalysis && !recapPatientsWithCaries) {
    return null;
  }

  return (
    <Card>
      <div>
        <VideaTypography
          color="white"
          variant="labelSmallStrong"
          component="h3"
        >
          <Trans>How many patients have AI findings?</Trans>
        </VideaTypography>
      </div>

      <FindingsRow>
        <VideaTypography color="white" variant="bodyExtrasmall" component="p">
          <Trans>Patients analyzed in the last month </Trans>
        </VideaTypography>
        <VideaTypography
          color="white"
          variant={
            recapPatientsWithAnalysis ? 'headingMedium' : 'headingExtrasmall'
          }
          component="span"
        >
          {recapPatientsWithAnalysis || `N/A`}
        </VideaTypography>

        <VideaTypography color="white" variant="bodyExtrasmall" component="p">
          <Trans>Patients with caries findings in the last month </Trans>
        </VideaTypography>
        <VideaTypography
          color="white"
          variant={
            recapPatientsWithCaries ? 'headingMedium' : 'headingExtrasmall'
          }
          component="span"
        >
          {recapPatientsWithCaries || 'N/A'}
        </VideaTypography>
      </FindingsRow>

      <SubCard>
        <SubcardTitleRow variant="labelSmallStrong">
          <TipsAndUpdatesOutlinedIcon data-testid="tips-icon" /> AI saves you
          time
        </SubcardTitleRow>
        <VideaTypography variant="labelSmallStrong" color={green400}>
          <Trans>
            We project you can save 3.4 min per patient using AI findings
            {Number(timeSavingsValue) > 0
              ? ` (or ${timeSavingsValue} min this month)`
              : ''}
          </Trans>
        </VideaTypography>
      </SubCard>
    </Card>
  );
};
